<template>
  <div class="logo-animated" ref="logoContainer">
    <div class="logo-frames" ref="logoFrames">
      <!-- SVG frames 1-8 -->
      <div 
        v-for="index in 8" 
        :key="index"
        class="logo-frame"
        :ref="el => setFrameRef(el as HTMLElement | null, index - 1)"
      >
        <img 
          :src="`/xw-logo-animation/xw-logo-animation-${index}.svg`" 
          :alt="`Logo animation frame ${index}`"
          class="logo-svg"
        />
      </div>
      
      <!-- Text frame -->
      <div 
        class="logo-frame text-frame"
        ref="textFrame"
      >
        <img 
          src="/xw-logo-animation/xw-logo-animation-0-text.svg" 
          alt="CrossWorks text"
          class="logo-text"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, onUnmounted, nextTick, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { gsap } from 'gsap'

interface Props {
  width?: number
  height?: number
  duration?: number
  repeat?: number
}

const props = withDefaults(defineProps<Props>(), {
  width: 300,
  height: 171,
  duration: 2,
  repeat: 1
})

const emit = defineEmits<{
  animationComplete: []
}>()

const logoContainer = ref<HTMLElement>()
const logoFrames = ref<HTMLElement>()
const textFrame = ref<HTMLElement>()
const frameRefs = ref<(HTMLElement | null)[]>([])

// Animation management
let timeline: gsap.core.Timeline | null = null
let isAnimationActive = ref(false)
let isUnmounted = false

// VueUse: Automatic cleanup of media query listener
const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

// Store frame references
const setFrameRef = (el: HTMLElement | null, index: number) => {
  if (el && !isUnmounted) {
    frameRefs.value[index] = el
  }
}

// Enhanced cleanup function
const cleanupAnimations = () => {
  if (timeline) {
    timeline.kill()
    timeline = null
  }
  
  // Reset all elements to initial state only if not unmounted
  if (!isUnmounted) {
    try {
      if (logoContainer.value) {
        gsap.set(logoContainer.value, { clearProps: "all" })
        logoContainer.value.style.willChange = 'auto'
      }
      
      frameRefs.value.forEach((frame) => {
        if (frame) {
          gsap.set(frame, { clearProps: "all" })
          frame.style.willChange = 'auto'
        }
      })
      
      if (textFrame.value) {
        gsap.set(textFrame.value, { clearProps: "all" })
        textFrame.value.style.willChange = 'auto'
      }
    } catch (error) {
      console.warn('GSAP cleanup error:', error)
    }
  }
  
  isAnimationActive.value = false
}

// Show final state immediately (for reduced motion)
const showFinalState = () => {
  if (isUnmounted) return
  
  try {
    frameRefs.value.forEach((frame) => {
      if (frame) {
        gsap.set(frame, { opacity: 1, x: 0, y: 0 })
      }
    })
    if (textFrame.value) {
      gsap.set(textFrame.value, { opacity: 1 })
    }
    emit('animationComplete')
  } catch (error) {
    console.warn('Final state setup error:', error)
  }
}

// Optimized animation start
const startAnimation = () => {
  if (!logoContainer.value || !frameRefs.value.length || !textFrame.value || isUnmounted) {
    return
  }

  // Skip animation if user prefers reduced motion
  if (prefersReducedMotion.value) {
    showFinalState()
    return
  }

  isAnimationActive.value = true

  try {
    // Add will-change for hardware acceleration
    if (logoContainer.value) {
      logoContainer.value.style.willChange = 'transform, opacity'
    }
    frameRefs.value.forEach((frame) => {
      if (frame) {
        frame.style.willChange = 'transform, opacity'
      }
    })
    if (textFrame.value) {
      textFrame.value.style.willChange = 'transform, opacity'
    }

    // Create main timeline with memory-efficient settings
    timeline = gsap.timeline({
      repeat: props.repeat - 1,
      onComplete: () => {
        if (isUnmounted) return
        
        isAnimationActive.value = false
        // Remove will-change after animation
        try {
          if (logoContainer.value) {
            logoContainer.value.style.willChange = 'auto'
          }
          frameRefs.value.forEach((frame) => {
            if (frame) {
              frame.style.willChange = 'auto'
            }
          })
          if (textFrame.value) {
            textFrame.value.style.willChange = 'auto'
          }
          emit('animationComplete')
        } catch (error) {
          console.warn('Animation complete cleanup error:', error)
        }
      },
      onUpdate: () => {
        // Check if we should abort animation mid-way
        if (isUnmounted) {
          cleanupAnimations()
        }
      }
    })

    // Define starting positions for each frame (X and Y axes) - bigger movements, more circular
    const getStartPosition = (index: number) => {
      // Reduce movement on mobile for better performance
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
      const scale = isMobile ? 0.6 : 1
      
      const positions = [
        { x: 0, y: 0 },        // Frame 0 - no movement, only fade
        { x: -180 * scale, y: -150 * scale },  // Frame 1 - far top left
        { x: 180 * scale, y: -150 * scale },   // Frame 2 - far top right  
        { x: -180 * scale, y: 150 * scale },   // Frame 3 - far bottom left
        { x: 180 * scale, y: 150 * scale },    // Frame 4 - far bottom right
        { x: -200 * scale, y: 0 },     // Frame 5 - far centre left
        { x: 200 * scale, y: 0 },      // Frame 6 - far centre right
        { x: 0, y: -200 * scale }      // Frame 7 - far top centre
      ]
      return positions[index] || { x: 0, y: 0 }
    }

    // Initial setup - hide all frames and position them
    frameRefs.value.forEach((frame, index) => {
      if (!frame || isUnmounted) return
      
      const startPos = getStartPosition(index)
      gsap.set(frame, { 
        opacity: 0,
        x: startPos.x,
        y: startPos.y
      })
    })

    // Set text frame initial state
    if (textFrame.value && !isUnmounted) {
      gsap.set(textFrame.value, { 
        opacity: 0,
        x: 0,
        y: 0
      })
    }

    // Build up frames cumulatively with smooth fade in + move in
    frameRefs.value.forEach((frame, index) => {
      if (!frame || isUnmounted) return
      
      const startTime = index * 0.15 // Each frame appears 0.15 seconds after the previous (slower)
      
      if (index === 0) {
        // Frame 0 - only fade in smoothly
        timeline?.to(frame, {
          opacity: 1,
          duration: 0.4,
          ease: "power1.inOut"
        }, startTime)
      } else {
        // Other frames - slide in from various directions with smooth fade in
        timeline?.to(frame, {
          opacity: 1,
          x: 0,
          y: 0,
          duration: 0.4,
          ease: "power1.inOut"
        }, startTime)
      }
    })

    // Show text frame after all logo frames are visible
    const textStartTime = frameRefs.value.length * 0.15 + 0.2
    timeline
      ?.to(textFrame.value, {
        opacity: 1,
        duration: 0.3,
        ease: "power1.inOut"
      }, textStartTime)
      
      // Hold complete logo + text for longer
      ?.to({}, { duration: 0.8 }, textStartTime + 0.3)
      
      // Fade out frames whilst moving back to starting positions
    frameRefs.value.forEach((frame, index) => {
      if (!frame || isUnmounted) return
      
      const startPos = getStartPosition(index)
      
      if (index === 0) {
        // Frame 0 - only fade out (no movement)
        timeline?.to(frame, {
          opacity: 0,
          duration: 0.5,
          ease: "power1.inOut"
        }, "+=0")
      } else {
        // Other frames - fade out whilst moving back to starting positions
        timeline?.to(frame, {
          opacity: 0,
          x: startPos.x,
          y: startPos.y,
          duration: 0.5,
          ease: "power1.inOut"
        }, "<") // Start at same time as previous frame
      }
    })
    
    // Text fades out in place
    timeline?.to(textFrame.value, {
      opacity: 0,
      duration: 0.5,
      ease: "power1.inOut"
    }, "<") // Start at same time as frames fade out

    // Add container entrance animation (only on first loop) - smoother, no bounce
    if (logoContainer.value && !isUnmounted) {
      gsap.fromTo(logoContainer.value, 
        { 
          scale: 0.9, 
          opacity: 0
        },
        { 
          scale: 1, 
          opacity: 1,
          duration: 0.6,
          ease: "power1.out"
        }
      )
    }
  } catch (error) {
    console.warn('Animation start error:', error)
    cleanupAnimations()
  }
}

// Complete cleanup function
const cleanup = () => {
  isUnmounted = true
  
  // Clean up animations
  cleanupAnimations()
  
  // Clear refs
  frameRefs.value = []
}

// VueUse automatically handles cleanup, but we still need to watch for changes
watch(prefersReducedMotion, (reducedMotion) => {
  if (reducedMotion && isAnimationActive.value) {
    // User switched to reduced motion while animation is running
    cleanupAnimations()
    showFinalState()
  }
})

onMounted(async () => {
  if (isUnmounted) return
  
  // Wait for next tick to ensure all refs are populated
  await nextTick()
  
  // Only start animation if we have all required elements and not unmounted
  if (!isUnmounted && logoContainer.value && frameRefs.value.length === 8 && textFrame.value) {
    startAnimation()
  }
})

onBeforeUnmount(cleanup)
onUnmounted(cleanup)
</script>

<style lang="scss" scoped>
@use '../assets/scss/variables' as *;

.logo-animated {
  position: relative;
  display: inline-block;
  width: v-bind("`${props.width}px`");
  height: v-bind("`${props.height}px`");
}

.logo-frames {
  position: relative;
  width: 100%;
  height: 100%;
  /* Remove overflow: hidden to allow bigger movements */
}

.logo-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
}

.logo-svg,
.logo-text {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(1); /* Force white colour */
}

.text-frame {
  .logo-text {
    filter: brightness(0) saturate(100%) invert(1) drop-shadow(0 0 20px rgba(255, 255, 255, 0.3));
  }
}

// Responsive adjustments
@media (max-width: $breakpoint-sm) {
  .logo-animated {
    width: 250px;
    height: 142px;
  }
}
</style>